import {
  type AuditRecommendationStatus,
  AuditRecommendationStatusSchema,
} from "@intelligems/ig-types/functions/opportunity-audit/records/recommendations";
import { isToolStep } from "@intelligems/ig-types/schemas/ai/tool-progress";
import { readAnalysis } from "@intelligems/ig-types/schemas/ai/ui-actions/audit-analyses";
import type { AuditBriefingRegionLanded } from "@intelligems/ig-types/schemas/ai/ui-actions/present-audit-briefing";
import { type UiMessageChunkDto } from "@intelligems/ig-types/schemas/ai/ui-message-chunk-dto";
import { type UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { createUserMessage } from "@src/features/ai/components/AiChat/utils/workflowUtils";

/**
 * Translates the agent SSE stream into the actions the chat already speaks.
 *
 * The audit is the first web chat on the agents framework, so nothing consumed
 * its stream before. Rather than build a second chat UI, this maps agent
 * frames onto `UiStreamAction` — the vocabulary `aiChatsReducer` has always
 * handled — so `ExecutionPanel`, the slots, the audit slice, its listener and
 * the whole right-hand panel keep working untouched.
 *
 * ## The two tracks
 *
 * The server sends assistant text one of two ways, never both, and the
 * difference is the reason this carries state at all:
 *
 * - **Live**: a run of `text.delta` envelopes, then a `cursor.advance` control
 *   frame. The driver buffers the deltas and flushes an aggregate row at each
 *   assistant boundary; that flush publishes `cursor.advance` *instead of* the
 *   content envelope, because the deltas already drew it. So on the live track
 *   `cursor.advance` is the end-of-message signal, and the only one there is.
 * - **Replay**: the persisted `message.assistant.text` aggregate, whole. No
 *   deltas. This is what a reconnect or a reload gets.
 *
 * Both have to produce the same conversation, so replay expands into the same
 * start/delta/end a live stream produces.
 *
 * ## Why it is not pure
 *
 * A message boundary is a property of the sequence, not of any one frame: a
 * delta cannot say whether it opens a message or continues one. Rather than
 * hide that in a closure, the cursor is an explicit argument the caller owns —
 * which also makes the sequences testable, and sequences are where the bugs
 * were on the workflow path.
 *
 * Returns an array because one frame can produce several actions, and an empty
 * array for frames the chat has no use for — dropping is a normal outcome, not
 * a failure.
 */
/**
 * The audit's own ask tool. Neither half of its call is a tool card — the
 * question it returns IS the card, so both arms below take the event over
 * rather than rendering it.
 */
const AUDIT_ASK_TOOL = "auditAsk";

export function toUiStreamActions(
  frame: unknown,
  cursor: AgentStreamCursor
): UiStreamAction[] {
  const control = readControl(frame);
  if (control) {
    return applyControl(control, cursor);
  }

  const envelope = readEnvelope(frame);
  if (!envelope) {
    return [];
  }

  const { event, turnId, dbSeq, streamSeq } = envelope;
  if (dbSeq) {
    cursor.afterSequence = dbSeq;
  }
  if (streamSeq) {
    cursor.afterStreamSeq = streamSeq;
  }

  // Every chunk carries node identity and a timestamp of its own, not just the
  // action wrapping it. One clock read per frame, so parts expanded from the
  // same event share a timestamp instead of straddling a millisecond boundary.
  const meta = { ...NODE, timestamp: Date.now() };

  switch (event.type) {
    case "turn.started":
      return [{ type: "start", turnId }];

    case "message.user":
      return userBubble(event, turnId, cursor);

    case "turn.completed":
      // A turn can end while a message is still open — the aggregate flush and
      // the terminal event race, and on replay there is no flush at all. Close
      // it here so the last bubble is never left mid-stream.
      cursor.endedTurnId = turnId;
      return [...closeOpenMessage(cursor, meta), { type: "finish", turnId }];

    case "turn.failed":
    case "turn.canceled":
      cursor.endedTurnId = turnId;
      return [
        ...closeOpenMessage(cursor, meta),
        {
          type: "setError",
          turnId,
          ...NODE,
          message: readErrorMessage(event),
        },
        { type: "finish", turnId },
      ];

    case "text.delta": {
      // The live track. `text` is the field the driver emits; there is no
      // `delta`.
      const text = readString(event, "text") ?? "";
      if (text === "") {
        return [];
      }

      const opened = openMessage(cursor, turnId, meta);
      return [
        ...opened.actions,
        part(turnId, opened.id, {
          ...meta,
          type: "text-delta",
          id: opened.id,
          delta: text,
        }),
      ];
    }

    case "message.assistant.text": {
      // The replay track: a finished message. `dbSeq` is unique per persisted
      // row, so it is the message's identity — two aggregates in one turn stay
      // two bubbles rather than merging into one.
      const id = `${turnId}:${dbSeq ?? "0"}`;
      return [
        part(turnId, id, { ...meta, type: "text-start", id }),
        part(turnId, id, {
          ...meta,
          type: "text-delta",
          id,
          delta: readString(event, "text") ?? "",
        }),
        part(turnId, id, { ...meta, type: "text-end", id }),
      ];
    }

    case "tool.progress": {
      // A step inside a call that is still running. `data` is opaque to the
      // framework by design, so it is checked here rather than trusted, and an
      // unrecognised payload is dropped — that is what opaque has to mean.
      const step = readUnknown(event, "data");
      if (!isToolStep(step)) {
        return [];
      }

      return [
        {
          type: "addToolProgress",
          turnId,
          messageId: `${turnId}:tools`,
          toolCallId: readString(event, "toolCallId") ?? "",
          step,
          ...NODE,
        },
      ];
    }

    case "tool.call":
      // The opening question travels as a real call/result pair so the
      // conversation the model reads back is well formed. Neither half is a
      // tool card though — the question is the card.
      if (readString(event, "toolName") === AUDIT_ASK_TOOL) {
        return closeOpenMessage(cursor, meta);
      }

      return [
        // A tool call ends the assistant message that preceded it — the driver
        // flushes the text buffer before emitting this. On the live track the
        // flush also sends `cursor.advance`, but not before this frame, so
        // closing here keeps the tool card out of the text bubble.
        ...closeOpenMessage(cursor, meta),
        part(turnId, `${turnId}:tools`, {
          ...meta,
          type: "tool-input-available",
          toolCallId: readString(event, "toolCallId") ?? "",
          toolName: readString(event, "toolName") ?? "",
          input: readUnknown(event, "input"),
        }),
      ];

    case "tool.result": {
      // The ask's result is not a tool card — it IS the question,
      // so it becomes the requirement that drives the option card. The agent
      // emits it after its closing message precisely so nothing clears it.
      if (readString(event, "toolName") === AUDIT_ASK_TOOL) {
        const nextAction = unwrapToolOutput(readUnknown(event, "output"));
        if (!isInputRequirement(nextAction)) {
          return [];
        }
        const userInput = nextAction.userInput as {
          options?: { value: string; label: string }[];
        };
        cursor.askOptions = (userInput.options ?? []).map(
          ({ value, label }) => ({ value, label })
        );
        return [{ type: "setNextAction", nextAction }];
      }

      // Everything else carries its output on the part the audit's listener
      // already reads to pick up ui-actions. Left wrapped: the listener does
      // its own unwrapping, and has since the workflow path.
      return [
        part(turnId, `${turnId}:tools`, {
          ...meta,
          type: "tool-output-available",
          toolCallId: readString(event, "toolCallId") ?? "",
          output: readUnknown(event, "output"),
        }),
      ];
    }

    default:
      // Reasoning, attempt.* and the passthrough arms have no chat rendering.
      // Silence is the right answer, not a warning.
      return [];
  }
}

/**
 * Where the client is in the stream, and what it is in the middle of.
 *
 * The two cursors are the reconnect contract: a dropped connection resumes
 * with `GET .../stream?afterSequence=&afterStreamSeq=`, and passing the pair
 * back is what stops a reconnect replaying what was already drawn.
 */
export interface AgentStreamCursor {
  /** Durable cursor: the last `dbSeq` seen, from an envelope or a cursor.advance. */
  afterSequence: string;
  /** Live-bus cursor: the last `streamSeq` seen. Absent on replayed envelopes. */
  afterStreamSeq: string;
  /** The assistant message currently being streamed, if one is open. */
  openMessageId: string | null;
  /** Counts messages opened this session, so ids stay distinct across turns. */
  messageCount: number;
  /**
   * The last turn a terminal event was seen for, which is what actually says
   * a turn is over. The socket closing says the same thing, but later and
   * less reliably — a proxy or a keep-alive can hold it open long after the
   * last frame, and one did: the server was leaving it open forever, which
   * left the composer disabled and swallowed every click.
   *
   * A turn id rather than a flag, so a replay runs past the earlier turns'
   * ends and stops at the one it waits for. `atRest` stands in for a turn id
   * when the server says nothing is running.
   */
  endedTurnId: string | null;
  /** The stream is replaying a stored conversation rather than a live turn. */
  replaying: boolean;
  /** The last ask's options, so a replayed answer shows the label it was chosen by. */
  askOptions: { value: string; label: string }[];
  /** The last turn that drew a merchant bubble; a retried turn writes its message again. */
  userTurnId: string | null;
}

/** What `endedTurnId` holds once the server says the session is at rest. */
export const SESSION_AT_REST = "atRest";

export const createAgentStreamCursor = (): AgentStreamCursor => ({
  afterSequence: "0",
  afterStreamSeq: "0",
  openMessageId: null,
  messageCount: 0,
  endedTurnId: null,
  replaying: false,
  askOptions: [],
  userTurnId: null,
});

/**
 * The chat models a turn as a tree of nodes because the workflow builder had
 * them. An agent is one thing, so it presents as one node rather than inventing
 * a hierarchy that nothing would read.
 */
const NODE = {
  nodeId: "opportunity-audit",
  nodeType: "other",
  nodeName: "Atlas",
} as const;

type ChunkMeta = typeof NODE & { timestamp: number };

/**
 * The chunk is built whole at the call site rather than assembled here, so the
 * compiler checks each literal against the right arm of the chunk union. A
 * helper that took the chunk minus its shared fields would have to cast to put
 * them back, which is exactly the check worth keeping.
 */
const part = (
  turnId: string,
  messageId: string,
  streamPart: UiMessageChunkDto
): UiStreamAction => ({
  type: "addStreamPart",
  turnId,
  messageId,
  streamPart,
  ...NODE,
});

/** Opens an assistant message if none is open, and reports its id either way. */
function openMessage(
  cursor: AgentStreamCursor,
  turnId: string,
  meta: ChunkMeta
): { id: string; actions: UiStreamAction[] } {
  if (cursor.openMessageId !== null) {
    return { id: cursor.openMessageId, actions: [] };
  }

  cursor.messageCount += 1;
  const id = `${turnId}:live:${cursor.messageCount}`;
  cursor.openMessageId = id;
  return {
    id,
    actions: [part(turnId, id, { ...meta, type: "text-start", id })],
  };
}

/** Closes the open assistant message, if there is one. Idempotent. */
function closeOpenMessage(
  cursor: AgentStreamCursor,
  meta: ChunkMeta
): UiStreamAction[] {
  const id = cursor.openMessageId;
  if (id === null) {
    return [];
  }

  cursor.openMessageId = null;
  const turnId = id.slice(0, id.indexOf(":live:"));
  return [part(turnId, id, { ...meta, type: "text-end", id })];
}

/**
 * Control frames are transport bookkeeping, with one exception that matters to
 * the conversation: `cursor.advance` is the live track's end-of-message.
 */
function applyControl(
  control: { kind: string; dbSeq?: string; oldestStreamSeq?: string },
  cursor: AgentStreamCursor
): UiStreamAction[] {
  const meta = { ...NODE, timestamp: Date.now() };
  switch (control.kind) {
    case "cursor.advance":
      if (control.dbSeq) {
        cursor.afterSequence = control.dbSeq;
      }
      return closeOpenMessage(cursor, meta);
    // The live buffer no longer holds what came after our stream cursor, so
    // the reconnect resumes from the oldest it does hold.
    case "retention.evicted":
      if (
        control.oldestStreamSeq &&
        laterStreamSeq(control.oldestStreamSeq, cursor.afterStreamSeq)
      ) {
        cursor.afterStreamSeq = control.oldestStreamSeq;
      }
      return [];
    // The session has nothing running, so this turn is over however it ended.
    case "session.at_rest":
      cursor.endedTurnId = SESSION_AT_REST;
      return [
        ...closeOpenMessage(cursor, meta),
        { type: "finish", turnId: "atlas" },
      ];
    default:
      return [];
  }
}

function readControl(
  frame: unknown
): { kind: string; dbSeq?: string; oldestStreamSeq?: string } | null {
  if (typeof frame !== "object" || frame === null) {
    return null;
  }
  const candidate = frame as {
    kind?: unknown;
    dbSeq?: unknown;
    oldestStreamSeq?: unknown;
  };
  if (typeof candidate.kind !== "string") {
    return null;
  }
  if (candidate.kind === "envelope") {
    return null;
  }

  return {
    kind: candidate.kind,
    dbSeq: typeof candidate.dbSeq === "string" ? candidate.dbSeq : undefined,
    oldestStreamSeq:
      typeof candidate.oldestStreamSeq === "string"
        ? candidate.oldestStreamSeq
        : undefined,
  };
}

function readEnvelope(frame: unknown): {
  event: object & { type: string };
  turnId: string;
  dbSeq?: string;
  streamSeq?: string;
} | null {
  if (typeof frame !== "object" || frame === null) {
    return null;
  }
  const candidate = frame as { kind?: unknown; envelope?: unknown };

  if (candidate.kind !== "envelope") {
    return null;
  }
  if (typeof candidate.envelope !== "object" || candidate.envelope === null) {
    return null;
  }

  const envelope = candidate.envelope as {
    event?: unknown;
    turnId?: unknown;
    dbSeq?: unknown;
    streamSeq?: unknown;
  };
  const { event, turnId } = envelope;
  if (typeof event !== "object" || event === null) {
    return null;
  }
  if (typeof (event as { type?: unknown }).type !== "string") {
    return null;
  }
  if (typeof turnId !== "string") {
    return null;
  }

  return {
    event: event as object & { type: string },
    turnId,
    dbSeq: typeof envelope.dbSeq === "string" ? envelope.dbSeq : undefined,
    streamSeq:
      typeof envelope.streamSeq === "string" ? envelope.streamSeq : undefined,
  };
}

/**
 * Tool outputs reach the wire in the AI SDK's result shape,
 * `{ type: "json", value: { results } }`, because that is what `BaseTool`
 * returns. The agent also emits one `tool.result` itself — the opening
 * question — which never went through a tool and so is not wrapped. Accept
 * both rather than making the caller know which it is looking at.
 */
function unwrapToolOutput(output: unknown): unknown {
  if (typeof output !== "object" || output === null) {
    return output;
  }

  const value = (output as { value?: unknown }).value;
  if (typeof value !== "object" || value === null) {
    return output;
  }

  const results = (value as { results?: unknown }).results;
  return results ?? output;
}

/**
 * Only accept a requirement the option card can actually render. A malformed
 * one would clear whatever question is on screen and replace it with nothing.
 *
 * **A choice with options, specifically.** The card draws one thing, and every
 * other requirement shape — a file upload, a catalog pick, a survey — renders
 * as an empty card while the turn sits waiting on an answer nobody can give.
 * Checking only that `userInput` was an object is what let a `shopify-pick`
 * through, and the merchant's whole question disappeared. `auditAsk`'s schema
 * now makes that unaskable, so this is the second lock rather than the only
 * one: a requirement reaching here in any other shape is a bug, and leaving
 * the previous question up is the better failure.
 */
function isInputRequirement(
  value: unknown
): value is Extract<UiStreamAction, { type: "setNextAction" }>["nextAction"] {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as { type?: unknown; userInput?: unknown };
  if (candidate.type !== "user_input") {
    return false;
  }
  const userInput = candidate.userInput as
    | { type?: unknown; options?: unknown }
    | null
    | undefined;
  return (
    typeof userInput === "object" &&
    userInput !== null &&
    userInput.type === "choice" &&
    Array.isArray(userInput.options) &&
    userInput.options.length > 0
  );
}

/** `turn.failed` carries a projected `WireError`, not a bare message. */
function readErrorMessage(event: object): string {
  const error = (event as { error?: unknown }).error;
  if (typeof error === "object" && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message !== "") {
      return message;
    }
  }
  if ((event as { type?: unknown }).type === "turn.canceled") {
    return "The audit was stopped.";
  }
  return "The audit could not finish.";
}

const readString = (event: object, key: string): string | undefined => {
  const value = (event as Record<string, unknown>)[key];
  return typeof value === "string" ? value : undefined;
};

const readUnknown = (event: object, key: string): unknown =>
  (event as Record<string, unknown>)[key];

/**
 * The status a shown recommendation was saved with, sent once saving it
 * finishes. Only live:
 * on replay it is stale, and the panel loads every status after.
 */
export function savedStatusOf(
  frame: unknown,
  replaying: boolean
): AuditRecommendationStatus | null {
  const envelope = readEnvelope(frame);
  if (replaying || envelope?.event.type !== "tool.progress") {
    return null;
  }
  const data = readUnknown(envelope.event, "data") as {
    type?: unknown;
    status?: unknown;
  } | null;
  if (data?.type !== "auditRecommendationSaved") {
    return null;
  }
  const status = AuditRecommendationStatusSchema.safeParse(data.status);
  return status.success ? status.data : null;
}

/**
 * A briefing region the briefing call has already read, sent as tool progress
 * so the panel shows it before the slower ones. Null for any other frame, and
 * for a region that does not read at today's version: the call's result
 * decides that region.
 */
export function briefingRegionOf(
  frame: unknown
): { call: string; landed: AuditBriefingRegionLanded } | null {
  const envelope = readEnvelope(frame);
  if (envelope?.event.type !== "tool.progress") {
    return null;
  }
  const data = readUnknown(envelope.event, "data") as {
    type?: unknown;
    region?: unknown;
    schemaVersion?: unknown;
    value?: unknown;
  } | null;
  if (
    data?.type !== "auditBriefingRegionLanded" ||
    typeof data.region !== "string"
  ) {
    return null;
  }
  const read = readAnalysis(
    data.region,
    typeof data.schemaVersion === "number" ? data.schemaVersion : undefined,
    data.value
  );
  if (read.type !== "read") {
    return null;
  }
  return {
    call: readString(envelope.event, "toolCallId") ?? "",
    landed: {
      type: "auditBriefingRegionLanded",
      region: data.region,
      value: read.value,
    } as AuditBriefingRegionLanded,
  };
}

/**
 * The merchant's message, drawn only on replay: a live turn adds its bubble
 * when it is sent. A turn retried by the server writes its message again, and
 * a `<system>` input is the panel's own, so neither draws.
 */
function userBubble(
  event: object,
  turnId: string,
  cursor: AgentStreamCursor
): UiStreamAction[] {
  if (!cursor.replaying || cursor.userTurnId === turnId) {
    return [];
  }
  const text = userText(readUnknown(event, "content"));
  if (text === null || text.trimStart().startsWith("<system>")) {
    return [];
  }
  cursor.userTurnId = turnId;
  const option = cursor.askOptions.find((it) => it.value === text);
  const { message } = createUserMessage(
    option
      ? { type: "choice", choice: text, label: option.label }
      : { type: "text", content: text }
  );
  return message
    ? [
        {
          type: "addMessage",
          turnId,
          message,
          nodeId: `${turnId}:user`,
          nodeName: "User Input",
        },
      ]
    : [];
}

/** A user message's text: a string, or the text parts of a part list. */
function userText(content: unknown): string | null {
  if (typeof content === "string") {
    return content;
  }
  if (!Array.isArray(content)) {
    return null;
  }
  const text = content
    .flatMap((part: { type?: unknown; text?: unknown }) =>
      part?.type === "text" && typeof part.text === "string" ? [part.text] : []
    )
    .join("");
  return text === "" ? null : text;
}

/** Whether bus id `a` (`<ms>-<seq>`) comes after `b`. */
function laterStreamSeq(a: string, b: string): boolean {
  const [aMs, aSeq] = a.split("-").map(Number);
  const [bMs, bSeq] = b.split("-").map(Number);
  return aMs !== bMs ? aMs > bMs : (aSeq ?? 0) > (bSeq ?? 0);
}
